import * as XLSX from "xlsx-js-style";
import Util from "@/js/common/Util";
import type { fldInfsProps } from "@/js/common/ui/GridTable";

export interface ExportOptions {
    fileName: string;
    sheetName?: string;
    columns: fldInfsProps[];
    rows: any[];
    groupHeaders?: { title: string; childFlds: string[] }[];
    exportCols?: string[];
    labelMap?: Record<string, string>;
    title?: string;
    subTitleLeft?: string;
    subTitleRight?: string;
}

/** 픽셀을 엑셀 넓이로 변환 */
const pxToXlsxWidth = (px?: number) => {
    if (!px || px <= 0) {
        return 10;
    }
    return Math.max(6, Math.round(px / 7));
};

/** 한글 확인 */
const isHangulChar = (ch: string) => /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(ch);

/** 한글(2)/그외(1) */
const stringWchKoreanRule = (s: string) => {
    if (!s) return 0;
    let w = 0;
    for (const ch of s) w += isHangulChar(ch) ? 2 : 1;
    return w;
};

/** 정렬 */
const toAlign = (v?: "left" | "center" | "right"): "left" | "center" | "right" =>
    (v === "left" || v === "right" || v === "center") ? v : "center";

/** 얇은 테두리 */
const thinBorder = {
    top: { style: "thin", color: { rgb: "000000" } },
    bottom: { style: "thin", color: { rgb: "000000" } },
    left: { style: "thin", color: { rgb: "000000" } },
    right: { style: "thin", color: { rgb: "000000" } },
};

/** 스타일 캐시 */
const headerStyleCache = new Map<"left" | "center" | "right", any>();
const dataStyleCache = new Map<"left" | "center" | "right", any>();

const getHeaderStyle = (align: "left" | "center" | "right") => {
    if (!headerStyleCache.has(align)) {
        headerStyleCache.set(align, {
            alignment: { horizontal: align, vertical: "center" },
            font: { name: "맑은 고딕", bold: true },
            fill: { fgColor: { rgb: "F2F2F2" } },
            border: thinBorder
        });
    }
    return headerStyleCache.get(align);
};

const getDataStyle = (align: "left" | "center" | "right") => {
    if (!dataStyleCache.has(align)) {
        dataStyleCache.set(align, {
            alignment: { horizontal: align, vertical: "center" },
            font: { name: "맑은 고딕" },
            border: thinBorder
        });
    }
    return dataStyleCache.get(align);
};

/** 추가: 제목 스타일 */
const getTitleStyle = () => {
    return {
        alignment: { horizontal: "center", vertical: "center" },
        font: { name: "맑은 고딕", bold: true, sz: 20 },
    };
};

/** 추가: 부제목 스타일 */
const getSubTitleStyle = (align: "left" | "center" | "right") => {
    return {
        alignment: { horizontal: align, vertical: "center" },
        font: { name: "맑은 고딕", bold: true, sz: 12 },
    };
};

/** 추가: 사용자 행/셀 스타일 병합 */
function mergeExcelStyle(baseStyle: any, rowData: any, fldNm?: string) {
    const rowStyle = rowData?.excelRowStyle || {};
    const cellStyle = (fldNm && rowData?.excelCellStyle?.[fldNm]) ? rowData.excelCellStyle[fldNm] : {};

    const fillColor = cellStyle.fillColor ?? rowStyle.fillColor;
    const fontColor = cellStyle.fontColor ?? rowStyle.fontColor;
    const bold = cellStyle.bold ?? rowStyle.bold;

    const mergedStyle = {
        ...baseStyle,
        font: {
            ...(baseStyle.font || {}),
            ...(bold !== undefined ? { bold } : {}),
            ...(fontColor ? { color: { rgb: fontColor } } : {}),
        },
    };

    if (fillColor) {
        mergedStyle.fill = { fgColor: { rgb: fillColor } };
    }

    return mergedStyle;
}

/** 내보내기 컬럼 선택 확정 */
function resolveExportColumns(columns: fldInfsProps[], exportCols?: string[]) {
    const usable = columns.filter(c => (c.type !== "el" || !!c.fldNm));
    if (!exportCols || exportCols.length === 0) {
        return usable;
    }
    return exportCols
        .map(k => usable.find(c => c.fldNm === k || c.label === k))
        .filter((c): c is fldInfsProps => !!c);
}

/** 헤더 구성 */
function buildHeader(
    columns: fldInfsProps[],
    groupHeaders?: { title: string; childFlds: string[] }[],
    labelMap?: Record<string, string>
) {
    const headerRows: string[][] = [];
    const merges: XLSX.Range[] = [];
    const labelOf = (c: fldInfsProps) => labelMap?.[c.fldNm || ""] ?? (c.label || c.fldNm || "");

    if (!groupHeaders || groupHeaders.length === 0) {
        headerRows.push(columns.map(labelOf));
        return { headerRows, merges };
    }

    const groupMap = new Map<string, string>();
    groupHeaders.forEach(g => g.childFlds.forEach(f => groupMap.set(f, g.title)));

    const top: string[] = [];
    const bottom: string[] = [];
    let col = 0;
    let curr: { title: string; start: number } | null = null;

    for (const c of columns) {
        const key = c.fldNm!;
        const grp = groupMap.get(key);

        if (!grp) {
            top.push(labelOf(c));
            bottom.push("");
            merges.push({ s: { r: 0, c: col }, e: { r: 1, c: col } });
            if (curr) {
                merges.push({ s: { r: 0, c: curr.start }, e: { r: 0, c: col - 1 } });
                curr = null;
            }
        } else {
            if (!curr || curr.title !== grp) {
                if (curr) {
                    merges.push({ s: { r: 0, c: curr.start }, e: { r: 0, c: col - 1 } });
                }
                curr = { title: grp, start: col };
            }
            top.push(grp);
            bottom.push(labelOf(c));
        }
        col++;
    }
    if (curr) merges.push({ s: { r: 0, c: curr.start }, e: { r: 0, c: col - 1 } });

    headerRows.push(top, bottom);
    return { headerRows, merges };
}

/** 셀 포맷터 */
function formatCell(c: fldInfsProps, row: any): string | number {
    const key = c.fldNm!;
    switch (c.type) {
        case "text":
            return Util.nvl(row[key], c.dfltVal ?? "");
        case "comma":
            return Util.isNotEmpty(row[key]) ? Number(row[key]) : "";
        case "phone":
            return Util.nvl(Util.cvtPhoneStrForm(row[key], "-"), c.dfltVal ?? "");
        case "date":
            return Util.nvl(Util.cvtDateStrForm(row[key], "-", "YYYYMMDD"), c.dfltVal ?? "");
        case "code":
            if (c.catCode) {
                return Util.getValToCode(c.catCode!, String(row[key] ?? "")) ?? (c.dfltVal ?? "");
            }
            return "";
        case "el":
            if (c.catCode) {
                return Util.getValToCode(c.catCode, String(row[key] ?? "")) ?? (c.dfltVal ?? "");
            }
            return "";
        case "seq":
            return "";
        default:
            return "";
    }
}

/** 엑셀로 내보내기 */
export function exportToXlsx(opts: ExportOptions) {
    const {
        fileName,
        sheetName = "Sheet1",
        columns,
        rows,
        groupHeaders,
        exportCols,
        labelMap,
        title,
        subTitleLeft,
        subTitleRight
    } = opts;

    // 컬럼 확정, 헤더/병합 생성
    const cols = resolveExportColumns(columns, exportCols);
    const { headerRows, merges } = buildHeader(cols, groupHeaders, labelMap);

    // 데이터 AOA
    const body = rows.map((r, rIdx) =>
        cols.map((c) => {
            if (c.type === "seq") return rIdx + 1;
            return formatCell(c, r);
        })
    );

    const titleRows: any[] = [];
    const titleMergeRows: XLSX.Range[] = [];
    let extraTopRows = 0;

    if (title) {
        titleRows.push([title, ...Array(Math.max(cols.length - 1, 0)).fill("")]);
        titleMergeRows.push({ s: { r: extraTopRows, c: 0 }, e: { r: extraTopRows, c: Math.max(cols.length - 1, 0) } });
        extraTopRows++;
    }

    if (subTitleLeft || subTitleRight) {
        const subRow = Array(cols.length).fill("");
        if (cols.length > 0) subRow[0] = subTitleLeft || "";
        if (cols.length > 0) subRow[cols.length - 1] = subTitleRight || "";
        titleRows.push(subRow);

        if (subTitleLeft && cols.length >= 3) {
            titleMergeRows.push({ s: { r: extraTopRows, c: 0 }, e: { r: extraTopRows, c: 2 } });
        }

        extraTopRows++;
    }

    const aoa = [
        ...titleRows,
        ...headerRows,
        ...body,
    ];

    // 시트 생성
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // 엑셀 최대 넓이 (가독성)
    const MAX_WCH = 80;

    // 넓이 적용
    (ws as any)["!cols"] = cols.map(c => {
        if (c.width) {
            return { wch: Math.min(MAX_WCH, pxToXlsxWidth(c.width)) };
        } else if (c.wAuto) {
            let maxWch = 0;
            const headerLabel = (c.label ?? c.fldNm ?? "").toString();
            maxWch = Math.max(maxWch, stringWchKoreanRule(headerLabel));

            for (const row of rows) {
                const cellText = String(formatCell(c, row) ?? "");
                maxWch = Math.max(maxWch, stringWchKoreanRule(cellText));
            }

            // 여유(2), 최소(6), 최대(80)
            const wch = Math.min(MAX_WCH, Math.max(2, maxWch + 6));
            return { wch };
        }
        return { wch: 10 };
    });

    // 병합 설정
    const allMerges = [
        ...titleMergeRows,
        ...merges.map((m) => ({
            s: { r: m.s.r + extraTopRows, c: m.s.c },
            e: { r: m.e.r + extraTopRows, c: m.e.c },
        })),
    ];
    if (allMerges.length > 0) (ws as any)["!merges"] = allMerges;

    // 추가: 행 높이
    const rowHeights: any[] = [];
    if (title) {
        rowHeights.push({ hpt: 28 });
    }
    if (subTitleLeft || subTitleRight) {
        rowHeights.push({ hpt: 20 });
    }
    for (let i = 0; i < headerRows.length; i++) {
        rowHeights.push({ hpt: 22 });
    }
    (ws as any)["!rows"] = rowHeights;

    // 스타일 적용
    const headerRowCount = headerRows.length;
    const colCount = cols.length;
    const headerStartRow = extraTopRows;

    // 추가: 타이틀 스타일
    if (title && colCount > 0) {
        const addr = XLSX.utils.encode_cell({ r: 0, c: 0 });
        if (!ws[addr]) {
            ws[addr] = { t: "s", v: title } as any;
        }
        (ws[addr] as any).s = getTitleStyle();
    }

    // 추가: 부제목 스타일
    if ((subTitleLeft || subTitleRight) && colCount > 0) {
        const subRowIndex = title ? 1 : 0;

        const leftAddr = XLSX.utils.encode_cell({ r: subRowIndex, c: 0 });
        if (!ws[leftAddr]) {
            ws[leftAddr] = { t: "s", v: subTitleLeft || "" } as any;
        }
        (ws[leftAddr] as any).s = getSubTitleStyle("left");

        const rightAddr = XLSX.utils.encode_cell({ r: subRowIndex, c: colCount - 1 });
        if (!ws[rightAddr]) {
            ws[rightAddr] = { t: "s", v: subTitleRight || "" } as any;
        }
        (ws[rightAddr] as any).s = getSubTitleStyle("right");
    }

    // 헤더 스타일
    if (headerRowCount === 1) { // 단일 헤더
        for (let c = 0; c < colCount; c++) {
            const addr = XLSX.utils.encode_cell({ r: headerStartRow, c });

            if (!ws[addr]) {
                ws[addr] = { t: "s", v: headerRows[0]?.[c] ?? "" } as any;
            }
            const hAlign = toAlign(cols[c].headAlign);
            (ws[addr] as any).s = getHeaderStyle(hAlign);
        }
    } else { // 그룹 헤더
        // 0행: 그룹 타이틀(또는 개별 헤더의 상단 셀)
        for (let c = 0; c < colCount; c++) {
            const addr = XLSX.utils.encode_cell({ r: headerStartRow, c });
            if (!ws[addr]) {
                ws[addr] = { t: "s", v: headerRows[0]?.[c] ?? "" } as any;
            }
            (ws[addr] as any).s = getHeaderStyle("center");
        }
        // 1행: 각 컬럼의 headAlign 사용
        for (let c = 0; c < colCount; c++) {
            const addr = XLSX.utils.encode_cell({ r: headerStartRow + 1, c });
            if (!ws[addr]) {
                ws[addr] = { t: "s", v: headerRows[1]?.[c] ?? "" } as any;
            }
            const hAlign = toAlign(cols[c].headAlign);
            (ws[addr] as any).s = getHeaderStyle(hAlign);
        }
    }

    // 데이터 스타일
    const dataStart = extraTopRows + headerRowCount; // 데이터 시작 행 index
    const dataRows = body.length;
    for (let r = 0; r < dataRows; r++) {
        const rowIndex = dataStart + r;
        for (let c = 0; c < colCount; c++) {
            const addr = XLSX.utils.encode_cell({ r: rowIndex, c });
            if (!ws[addr]) {
                ws[addr] = { t: "s", v: "" } as any;
            }
            const dAlign = toAlign(cols[c].dataAlign);
            const baseStyle = getDataStyle(dAlign);
            const style = mergeExcelStyle(baseStyle, rows[r], cols[c].fldNm);
            if (cols[c].type === "comma") {
                style.numFmt = "#,##0";
            }
            (ws[addr] as any).s = style;
        }
    }

    // 시트/파일 저장
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const safe = fileName.replace(/[\\/:*?"<>|]+/g, "_");
    XLSX.writeFile(wb, `${safe}.xlsx`);
}